import { defineStore } from 'pinia'
import { AddVerifiableMethodRequest,
    AddVerifiableMethodResponse,
    AnswerChallengeRequest,
    AvailableVerificationMethodsResponse,
    VerificationActionResult,
    VerificationChallengeSummary,
    VerificationChallengeSummaryResponse,
    RemoveVerifiableMethodRequest,
    RemoveVerifiableMethodResponse,
    UpdateVerifiableMethodRequest,
    UpdateVerifiableMethodResponse,
    VerificationMethod,
    VerificationMethodsResponse } from '@/api/requests/verification/verificationResponses'
import { addVerifiableMethod as addVerifiableMethodRequest,
    answerVerificationChallenge as answerVerificationChallengeRequest,
    getAvailableVerificationMethods,
    getVerifiableMethods,
    removeVerifiableMethod as removeVerifiableMethodRequest,
    updateVerifiableMethod as updateVerifiableMethodRequest } from '@/api/requests/verification/verification'
import { useInterruptedRequestStore } from '@/store/interruptedRequest'
import { isVerificationMethodStatusSuccessful } from '@/utils/verification'
import { useAppStore } from '@/store/app'

export const useVerificationStore = defineStore('verification', {
    state: () => {
        return {
            requiresVerification: false as boolean,
            verificationUuid: localStorage.getItem('verificationUuid') ?? null as string | null,
            verificationMethods: [] as Array<VerificationMethod>,
            availableMethods: [] as Array<VerificationMethod>,
            actionResults: [] as Array<VerificationActionResult>,
            selectedMethodType: null as string | null,
            selectedMethodName: null as string | null,
        }
    },
    actions: {
        getVerificationUuid(){
            return this.verificationUuid ?? localStorage.getItem('verificationUuid') ?? null
        },
        removeVerificationUuid(){
            localStorage.removeItem('verificationUuid')
            this.verificationUuid = null
        },
        setVerificationUuid(uuid: string){
            localStorage.setItem('verificationUuid', uuid)
            this.verificationUuid = uuid
        },
        async loadVerifiableMethods(){
            const response = await getVerifiableMethods() as VerificationMethodsResponse

            this.verificationMethods = response.data

            return response
        },
        async loadAvailableVerificationMethods(){
            const response = await getAvailableVerificationMethods() as AvailableVerificationMethodsResponse

            this.availableMethods = response.data

            return response
        },
        async addUserVerifiableMethod(payload: AddVerifiableMethodRequest, processVerification = true){
            const response = await addVerifiableMethodRequest(payload) as AddVerifiableMethodResponse
            const data = response.data

            if (processVerification && this.isVerificationChallengeSummary(data)) {
                this.setVerificationUuid(data.verificationUuid)
                this.actionResults = data.actionResults ?? []
                this.registerAddMethodRetryIfNeeded(payload, data)

                await this.recalculateChallengeState()
            }

            return response
        },
        async answerVerificationChallenge(payload: AnswerChallengeRequest){
            const response = await answerVerificationChallengeRequest(payload) as VerificationChallengeSummaryResponse

            this.setVerificationUuid(response.data.verificationUuid)
            const answeredActionResults = response.data.actionResults ?? []

            if (!answeredActionResults.length) {
                await this.recalculateChallengeState()

                return response
            }

            if (!this.actionResults.length) {
                this.actionResults = answeredActionResults

                await this.recalculateChallengeState()

                return response
            }

            this.actionResults = this.actionResults.map(existingResult => {
                const answeredResult = answeredActionResults.find(result => result.action === existingResult.action)

                if (!answeredResult) {
                    return existingResult
                }

                const updatedMethodResults = existingResult.methodResults.map(methodResult => {
                    const answeredMethod = answeredResult.methodResults.find(result =>
                        result.methodName === methodResult.methodName &&
                        result.methodType === methodResult.methodType
                    )

                    if (!answeredMethod) {
                        return methodResult
                    }

                    return {
                        ...methodResult,
                        challengeStatus: answeredMethod.challengeStatus,
                        challengeToken: answeredMethod.challengeToken,
                    }
                })

                return {
                    ...existingResult,
                    challengesPending: answeredResult.challengesPending,
                    methodResults: updatedMethodResults,
                }
            })

            await this.recalculateChallengeState()

            return response
        },
        async recalculateChallengeState(){
            const hasPending = this.actionResults.some(result => {
                if (result.challengesPending === false) {
                    return false
                }

                if (result.challengesPending) {
                    return true
                }

                if (!Array.isArray(result.methodResults) || !result.methodResults.length) {
                    return false
                }

                return result.methodResults.some(methodResult =>
                    !isVerificationMethodStatusSuccessful(methodResult.challengeStatus)
                )
            })

            if (hasPending) {
                this.requiresVerification = true

                return
            }

            this.requiresVerification = false

            this.actionResults = []
            this.selectedMethodName = null
            this.selectedMethodType = null

            const appStore = useAppStore()

            if (appStore.verificationActionCallback && appStore.verificationActionCallbackReplacesRetry) {
                try {
                    const response = await appStore.verificationActionCallback()

                    useInterruptedRequestStore().resolvePending(response)
                } catch (error) {
                    useInterruptedRequestStore().rejectPending(error)

                    throw error
                } finally {
                    appStore.verificationActionCallback = null
                    appStore.verificationActionCallbackReplacesRetry = false
                }

                return
            }

            await useInterruptedRequestStore().retryRequest().then(async () => {
                if (appStore.verificationActionCallback) {
                    await appStore.verificationActionCallback()
                    appStore.verificationActionCallback = null
                }
            }).catch(() => {
                // request retry errors handled where they originate
            })
        },
        async removeUserVerifiableMethod(payload: RemoveVerifiableMethodRequest){
            return await removeVerifiableMethodRequest(payload) as RemoveVerifiableMethodResponse
        },
        async updateUserVerifiableMethod(payload: UpdateVerifiableMethodRequest){
            return await updateVerifiableMethodRequest(payload) as UpdateVerifiableMethodResponse
        },
        isVerificationChallengeSummary(data: VerificationMethod | VerificationChallengeSummary): data is VerificationChallengeSummary {
            return data !== null &&
                typeof data === 'object' &&
                'verificationUuid' in data &&
                'actionResults' in data
        },
        registerAddMethodRetryIfNeeded(payload: AddVerifiableMethodRequest, summary: VerificationChallengeSummary) {
            const isConfigureChallenge = summary.actionResults
                ?.some(result => result.action === 'configure')

            if (!summary.challengesPending || isConfigureChallenge || payload.answer) {
                return
            }

            const appStore = useAppStore()

            appStore.verificationActionCallbackReplacesRetry = true
            appStore.verificationActionCallback = () => this.addUserVerifiableMethod({
                ...payload,
                verificationUuid: this.getVerificationUuid(),
            })
        },
    },
})
